import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  input,
  Input,
  OnChanges,
  OnDestroy,
  output,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import {
  availableInventoryTooltipLabel,
  trueDemandInventoryTooltipLabel,
} from '@epicuro-next/constants/features/localization';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { selectAccountNowInventoryLeadTimeInDays } from '@epicuro-next/state/accounts-state';
import {
  CellInventory,
  ConfigStripStateConfigurationKeys,
  ConfigStripStateConfigurations,
  FEATURE_NAME,
  selectAvailabilityRangesRule,
  selectBackOrderEnabledRule,
  selectCellTotalsFilterRule,
  selectCurrentlyActiveOrderType,
  selectHideInventoryCellTotals,
  selectInventoryColorMapper,
  selectMaxDisplayedInventory,
  selectMaxUnlimitedQuantityOrder,
  selectPrioritizeMaxUnlimitedQuantityShown,
  selectShowBackOrderFactoryRule,
  selectShowInfinitySign,
  selectSkipInventoryCheckEnabled,
} from '@epicuro-next/state/config-strip-state';
import { isEqual, isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { OrderType } from 'graphql-api/gql-api-interfaces';
import { combineLatest, ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  shareReplay,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

import {
  backOrdersOnly,
  filterOutInventoriesBasedOnRule,
  getCellColor,
  splitTotals,
} from '../../shared/cell/cell-inventory';

@Component({
  selector: 'epc-grid-cell',
  templateUrl: './grid-cell.component.html',
  styleUrls: ['./grid-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => GridCellComponent),
    },
  ],
  standalone: false,
})
export class GridCellComponent implements OnDestroy, OnChanges {
  public inventory = input<CellInventory>({
    inventories: [],
    totals: 0,
    hasTrueDemandInventory: false,
  });

  @Input() public readonly = false;

  /** shows either material is locked */
  public isLocked = input(false);

  @Input()
  public showInventoryPlaceholderOnFocus = true;

  @Input()
  public sizeRunsCell = false;

  @Input()
  public currentCellValue = 0;

  /** define already ordered quantity */
  @Input()
  public inBasketQuantity = 0;

  @Output()
  public updateTotals = new EventEmitter<number>();

  public updateTrueDemandTotals = output<number>();

  @Output() public cellClicked = new EventEmitter<void>();

  @Output() public cellKeyupEnter = new EventEmitter<void>();

  @Output() public clipboardPasted = new EventEmitter<string[][]>();

  public quantity = signal<number | null>(null);

  public readonly min = Math.min;
  public readonly max = Math.max;
  public readonly infinity = Infinity;

  public readonly backOrdersOnly = computed(() =>
    backOrdersOnly(this.inventory()),
  );
  public readonly inventoryTotals = computed(() => this.inventory().totals);
  public readonly inventories = computed(() => this.inventory().inventories);
  public readonly hasTrueDemandInventory = computed(
    () => this.inventory().hasTrueDemandInventory,
  );
  public readonly splitQuantityToNormalAndTrueDemand = computed(() =>
    splitTotals(this.quantity() ?? 0, this.inventory()),
  );
  public readonly hasConsumedTrueDemand = computed(
    () => this.splitQuantityToNormalAndTrueDemand().length > 1,
  );

  @HostBinding('class.active-cell')
  public cellInFocus = false;

  public quantityControl: UntypedFormControl = new UntypedFormControl();

  @HostBinding('class.empty-cell')
  public get cellEmpty() {
    return (
      this.isLocked() ||
      (!this.inventoryTotals() &&
        !this.quantity() &&
        !this.hasTrueDemandInventory())
    );
  }

  private destroy$ = new Subject<void>();
  private cellReady$ = new ReplaySubject<void>(1);
  public readonly availableInventoryTooltipLabel =
    availableInventoryTooltipLabel;
  public readonly trueDemandInventoryTooltipLabel =
    trueDemandInventoryTooltipLabel;

  public readonly maxUnlimitedQuantityOrder$ = this.store.select(
    selectMaxUnlimitedQuantityOrder,
  );

  private readonly prioritizeMaxUnlimitedQuantityShown$ = this.store.select(
    selectPrioritizeMaxUnlimitedQuantityShown,
  );

  private readonly maxDisplayedInventory$ = this.store.select(
    selectMaxDisplayedInventory,
  );

  public readonly skipInventoryCheck = toSignal(
    this.store.pipe(select(selectSkipInventoryCheckEnabled), map(Boolean)),
  );

  public readonly showInfinitySign$ = this.store.select(selectShowInfinitySign);

  private readonly showBackOrderFactoryRule = toSignal(
    this.store.select(selectShowBackOrderFactoryRule),
  );

  public readonly showBackOrderFactory = computed(
    () => this.showBackOrderFactoryRule() && this.backOrdersOnly(),
  );

  private readonly availabilityColorRanges$ = this.store.select(
    selectAvailabilityRangesRule,
  );

  private readonly cellTotalsFilter$ = this.store.select(
    selectCellTotalsFilterRule,
  );

  private readonly hideInventoryCellTotals$ = this.store.select(
    selectHideInventoryCellTotals,
  );

  private cellTotals$ = combineLatest([
    this.cellReady$.asObservable(),
    this.cellTotalsFilter$,
    this.store.select(selectCurrentlyActiveOrderType),
    this.store.select(selectAccountNowInventoryLeadTimeInDays),
  ]).pipe(
    map(
      ([, cellTotalsFilter, selectedOrderType, nowInventoryLeadTimeInDays]: [
        void,
        string,
        OrderType,
        number,
      ]) =>
        filterOutInventoriesBasedOnRule(
          this.inventory(),
          cellTotalsFilter,
          selectedOrderType,
          nowInventoryLeadTimeInDays,
        ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public displayedCellTotals$ = this.cellTotals$.pipe(
    withLatestFrom(
      this.showInfinitySign$,
      this.maxUnlimitedQuantityOrder$,
      this.maxDisplayedInventory$,
      this.hideInventoryCellTotals$,
      this.prioritizeMaxUnlimitedQuantityShown$,
    ),
    map(
      ([
        totals,
        showInfinitySign,
        maxUnlimitedQuantityOrder,
        maxDisplayedInventory,
        hideInventoryCellTotals,
        prioritizeMaxUnlimitedQuantityShown,
      ]: [number, boolean, number, number, boolean, boolean]) => {
        if (hideInventoryCellTotals) {
          return '';
        }
        if (totals === Infinity) {
          if (showInfinitySign) {
            return Infinity;
          }
          return '0';
        }

        if (
          prioritizeMaxUnlimitedQuantityShown &&
          totals > maxUnlimitedQuantityOrder
        ) {
          return `${maxUnlimitedQuantityOrder}`;
        }

        if (totals > maxDisplayedInventory) {
          return `${maxDisplayedInventory}+`;
        }

        return totals;
      },
    ),
    takeUntil(this.destroy$),
  );

  public cellInputClass$ = combineLatest([
    this.cellReady$.asObservable(),
    this.cellTotals$,
    this.availabilityColorRanges$,
  ]).pipe(
    map(([, totals, availabilityColorRanges]) =>
      getCellColor(
        this.isLocked(),
        totals,
        availabilityColorRanges,
        'placeholder-',
      ),
    ),
    takeUntil(this.destroy$),
  );

  public cellSpanClass$ = this.cellReady$.asObservable().pipe(
    withLatestFrom(this.cellTotals$, this.availabilityColorRanges$),
    map(([, totals, availabilityColorRanges]) =>
      getCellColor(this.isLocked(), totals, availabilityColorRanges, 'text-'),
    ),
  );

  public inventoryColorMapper$ = this.store.pipe(
    select(selectInventoryColorMapper),
  );

  private readonly backOrderEnabled$ = this.store.pipe(
    select(selectBackOrderEnabledRule),
  );

  public backOrderNotEnabled$ = this.backOrderEnabled$.pipe(
    map((isBackOrderEnabled) => !isBackOrderEnabled),
  );

  public checkInventoryForBackOrder$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripStateConfigurationKeys.CheckInventoryForBackOrder,
        ConfigStripStateConfigurations
      >(
        FEATURE_NAME,
        ConfigStripStateConfigurationKeys.CheckInventoryForBackOrder,
        false,
      ),
    ),
  );

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {
    this.quantityControl.valueChanges
      .pipe(debounceTime(600), takeUntil(this.destroy$))
      .subscribe((quantity: number) => {
        const trueDemandTotal =
          splitTotals(quantity ?? 0, this.inventory())?.[1] ?? 0;
        const prevTrueDemandTotal =
          splitTotals(this.quantity() ?? 0, this.inventory())?.[1] ?? 0;

        this.updateTrueDemandTotals.emit(trueDemandTotal - prevTrueDemandTotal);
        this.updateTotals.emit((quantity || 0) - (this.quantity() || 0));
        this.quantity.set(quantity);
        this.onChange(quantity);
      });
  }

  public blur(parentPassedValue?: number) {
    const quantity = parentPassedValue ?? this.quantityControl.value;
    /***
     * When it's 0 we do not want to show it as a value in input but want instead show:
     * 1. factory icon;
     * 2. or infinity icon;
     * 3. or inventories available
     *  **/
    if (quantity === 0 || quantity === null) {
      this.quantityControl.setValue('', { emitEvent: false });
      this.cdr.detectChanges();
    }
  }

  public onChange: (quantity: number) => void = () => {
    // interface; comment for Sonar
  };

  public onPaste(pasteEvent: ClipboardEvent) {
    pasteEvent.preventDefault();
    const text = pasteEvent.clipboardData?.getData('text');
    if (text) {
      const result = text
        .replace(/\n$/g, '')
        .split('\n')
        .map((row: string) => row.split('\t'));
      this.clipboardPasted.emit(result);
    }
  }

  public writeValue(quantity: number): void {
    this.quantity.set(quantity);
    const currentValue = this.quantityControl.value;
    if (quantity !== currentValue) {
      this.cellInFocus = false;
      this.updateTotals.emit((quantity || 0) - (currentValue || 0));
      this.updateTrueDemandTotals.emit(
        (splitTotals(quantity || 0, this.inventory())?.[1] ?? 0) -
          (splitTotals(
            this.quantityControl.value || 0,
            this.inventory(),
          )?.[1] ?? 0),
      );
      this.quantityControl.setValue(quantity, { emitEvent: false });
      this.cdr.detectChanges();
    }

    this.blur(quantity);
  }

  public registerOnChange(fn: () => {}): void {
    this.onChange = fn;
  }

  public registerOnTouched(): void {
    // interface; comment for Sonar
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      !isEqual(
        changes.inventory?.currentValue,
        changes.inventory?.previousValue,
      ) ||
      !isEqual(
        changes.currentCellValue?.currentValue,
        changes.currentCellValue?.previousValue,
      )
    ) {
      this.cellReady$.next();
    }
  }

  public ngOnInit() {
    combineLatest([this.inventoryColorMapper$, this.cellTotals$])
      .pipe(
        filter(([inventoryColorMapper, _]) =>
          isNonNullish(inventoryColorMapper),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(
        ([inventoryColorMapper, cellTotals]: [
          { limit: number; color: string; text: string }[],
          number,
        ]) => {
          if (!cellTotals) {
            return;
          }
          const colorInfo =
            inventoryColorMapper.find((info) => cellTotals < info.limit) ||
            inventoryColorMapper[inventoryColorMapper.length - 1];

          if (colorInfo.color) {
            this.elementRef.nativeElement.style.setProperty(
              '--config-strip-size-grid-cell-inventory-color',
              colorInfo.color,
            );
          }

          if (colorInfo.text) {
            this.elementRef.nativeElement.style.setProperty(
              '--config-strip-size-grid-cell-color',
              colorInfo.text,
            );

            this.elementRef.nativeElement.style.setProperty(
              '--config-strip-size-grid-cell-empty-color',
              colorInfo.text,
            );
          }
        },
      );
  }

  public ngOnDestroy() {
    this.destroy$.next();
  }
}
